import SEO from "@/components/SEO";
import { facility1, facility2, facility3, facility4, facility5, facilitySrcSet } from "@/lib/cdn-images";
import { webPageSchema, breadcrumbSchema } from "@/lib/organization-schema";

const GIVE_URL = "https://give.abbashouse.org";

const steps = [
  {
    num: "01",
    title: "Warehouse Conversion",
    desc: "We will convert underutilized warehouses into fully equipped Community Housing Centers — warm, safe, and dignified spaces across the nation.",
  },
  {
    num: "02",
    title: "Private Pods",
    desc: "Each member will receive their own private pod complete with a clean bed, a comfortable sitting area, and a TV — personal space in a community setting.",
  },
  {
    num: "03",
    title: "Shared Amenities",
    desc: "Members will enjoy shared amenities: a gym, cafeteria, full kitchens, laundry rooms, personal lockers, and office space for job searching and meetings.",
  },
  {
    num: "04",
    title: "90-Day Transition Program",
    desc: "Every new member will enter our 90-day program — free housing and meals, clothing, healthcare, documentation assistance, job placement, and trade school or college enrollment.",
  },
  {
    num: "05",
    title: "Independent Membership — $750/mo",
    desc: "After graduating the 90-day program, members will transition to independent status. A monthly membership fee of $750 will cover their private pod, cafeteria meal plan, and continued case management — a fraction of market-rate rent.",
  },
  {
    num: "06",
    title: "Direct Entry Program",
    desc: "Those who are already employed will be able to bypass the 90-day program and enter our Direct Entry track — providing a private pod, cafeteria plan, and access to all community center amenities. Case management will be available but not required for working members.",
  },
  {
    num: "07",
    title: "Long-Term Stability",
    desc: "Members who need more time will be welcome to stay. Our goal is genuine restoration — not a revolving door, but a true path home.",
  },
];

const galleryImages = [
  { src: facility1, alt: "Exterior Community Housing Center" },
  { src: facility2, alt: "Private Pod Interior" },
  { src: facility3, alt: "Community Gym" },
  { src: facility4, alt: "Recreation Space" },
  { src: facility5, alt: "Cafeteria & Kitchen" },
];

export default function About() {
  return (
    <main>
      <SEO
        title="About Us"
        description="Learn about Abba's House Communities — a Christian nonprofit converting warehouses into dignified Community Housing Centers with 90-day transition programs for the homeless."
        canonical="https://abbashouse.org/about"
        jsonLd={[
          webPageSchema({ name: "About Us — Abba's House Communities", description: "Learn about Abba's House Communities — a Christian nonprofit converting warehouses into dignified Community Housing Centers.", url: "https://abbashouse.org/about" }),
          breadcrumbSchema([
            { name: "Home", url: "https://abbashouse.org/" },
            { name: "About Us", url: "https://abbashouse.org/about" },
          ]),
        ]}
      />
      <section className="bg-primary text-primary-foreground py-24 md:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <p className="text-sm font-semibold tracking-[0.3em] uppercase opacity-50 mb-6">Who We Are</p>
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight leading-none mb-8 max-w-3xl">
            We Exist to End Homelessness.
          </h1>
          <p className="text-lg sm:text-xl opacity-70 leading-relaxed max-w-2xl">
            Abba's House Communities is a nonprofit Christian ministry answering the call of Matthew 25:35–40 — to house the homeless, welcome the stranger, and restore dignity to the displaced.
          </p>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">A Christian Foundation</h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                Abba's House Communities was founded on the belief that every human being — regardless of their past or present circumstances — deserves a safe place to sleep, heal, and rebuild.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                As a nonprofit Christian ministry, we operate under the conviction that God calls us to care for the vulnerable. Our slogan isn't just a tagline — it's a promise: <strong className="text-foreground">A Home For Everyone.</strong>
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">
                We are raising funds to build Community Housing Centers nationwide — converting warehouses into places of refuge, restoration, and renewal.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img src={facility1} srcSet={facilitySrcSet(facility1)} sizes="(min-width: 1024px) 50vw, 100vw" alt="Abba's House Community Center" width={1280} height={720} className="w-full h-full object-cover min-h-64" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-20 md:py-28 bg-secondary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">The Problem We're Solving</h2>
          <p className="text-muted-foreground text-lg leading-relaxed mb-6 max-w-3xl mx-auto">
            Homelessness is not a character flaw — it's a systemic failure. The cycle is relentless: no home means no address, no address means no job, no job means no home.
          </p>
          <p className="text-muted-foreground text-lg leading-relaxed mb-12 max-w-3xl mx-auto">
            But homelessness doesn't only affect the unemployed. Many people with steady jobs are simply <strong className="text-foreground">priced out of the market</strong> — landlords routinely require income of 3× the monthly rent, making housing inaccessible even for those who are working full-time. You can be employed and still homeless.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { stat: "18%", label: "Increase in homelessness in 2024" },
              { stat: "Rising", label: "Rents fueled by private equity displacement" },
              { stat: "0", label: "Job offers without a mailing address" },
            ].map((item) => (
              <div key={item.label} className="bg-primary text-primary-foreground rounded-2xl p-8">
                <div className="text-5xl font-black mb-3">{item.stat}</div>
                <p className="text-sm opacity-70 leading-snug">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">How It Works</h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              From street to stability — a clear, compassionate path forward.
            </p>
          </div>
          <div className="flex flex-col gap-6 max-w-4xl mx-auto">
            {steps.map((step) => (
              <div key={step.num} className="flex gap-6 md:gap-10 items-start border border-border rounded-2xl p-6 md:p-8 hover:border-foreground transition-colors">
                <span className="text-5xl font-black text-muted-foreground/30 flex-shrink-0 leading-none">{step.num}</span>
                <div>
                  <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-20 md:py-28 bg-secondary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold tracking-[0.3em] uppercase text-muted-foreground mb-4">Our Vision</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">What Our Centers Will Look Like</h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              We are actively fundraising to open our first Community Housing Center. These images represent the concept — safe, dignified, community-centered spaces we are working to build.
            </p>
            <p className="text-sm text-muted-foreground/70 italic mt-3 max-w-xl mx-auto">
              *Disclaimer: The images shown are conceptual renderings only and do not represent a final design. Actual facilities may differ in appearance.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {galleryImages.map((img, i) => (
              <div key={i} className={`rounded-2xl overflow-hidden ${i === 0 ? "col-span-2" : ""}`}>
                <img
                  src={img.src}
                  srcSet={facilitySrcSet(img.src)}
                  sizes="(min-width: 768px) 33vw, 50vw"
                  alt={img.alt}
                  width={1280}
                  height={720}
                  className="w-full h-56 md:h-72 object-cover hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scripture Section */}
      <section className="bg-primary text-primary-foreground py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
          <p className="text-sm font-semibold tracking-[0.3em] uppercase opacity-50 mb-12">Our Call</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 text-left">
            <div className="border-l-4 border-primary-foreground/30 pl-8">
              <blockquote className="text-lg font-light italic leading-relaxed opacity-85 mb-4">
                "For I was hungry and you gave me food, I was thirsty and you gave me drink, I was a stranger and you welcomed me..."
              </blockquote>
              <cite className="text-xs font-bold tracking-widest opacity-40 not-italic">— MATTHEW 25:35–40</cite>
            </div>
            <div className="border-l-4 border-primary-foreground/30 pl-8">
              <blockquote className="text-lg font-light italic leading-relaxed opacity-85 mb-4">
                "Protect the rights of the poor and the orphans. Be fair to the needy and the helpless. Rescue the poor and helpless. Save them from the power of the wicked."
              </blockquote>
              <cite className="text-xs font-bold tracking-widest opacity-40 not-italic">— PSALMS 82:3–4 (ERV)</cite>
            </div>
          </div>
          <div className="mt-16">
            <a
              href={GIVE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-10 py-5 rounded-full bg-primary-foreground text-primary text-lg font-bold tracking-wide hover:opacity-90 transition-opacity"
            >
              Donate to Help End Homelessness — Partner With Abba's House
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}